import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DisneyService } from '@core/services';
import { IDisneyClusterTickets } from '@core/models/modals';
import { MB_DIALOG_DATA, MbDialogRef, MbDialogHeader, MbDialogBody, MbIconModule, MbAdvice, MbButton } from '@am/moebius';
import { DialogWrapperComponent } from '@modals/dialog-wrapper/dialog-wrapper.component';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-modal-disney-cluster-tickets-info',
  standalone: true,
  imports: [CommonModule, MbDialogHeader, MbDialogBody, MbIconModule, MbAdvice, MbButton, SharedModule],
  templateUrl: './disney-cluster-tickets-info.component.html',
  styleUrls: ['./disney-cluster-tickets-info.component.scss'],
})
export class ModalDisneyClusterTicketsInfoComponent
  extends DialogWrapperComponent
  implements OnInit
{
  public showTicketSelector = false;
  public ticketDays = 0;
  public tickets: any;
  public supplements: any;
  public selectedTicketDays: any;
  public selectedTicket: any;
  public hasSameTicketForPlan: boolean;
  public samePlanWithPromo: boolean;
  public commission: any;

  constructor(
    private disneyService: DisneyService,
    private dialogRef: MbDialogRef<ModalDisneyClusterTicketsInfoComponent>,
    @Inject(MB_DIALOG_DATA) public dialogData: IDisneyClusterTickets,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.getSupplements();
    this.orderTickets();
  }

  private getSupplements() {
    // Merge suplements by Days and Ticket Type
    this.supplements = {};

    let rates = [];
    if (this.dialogData.selectedPlan.code === 'ROOM_ONLY') {
      rates = this.dialogData.selectedPlan.rates;
    } else {
      this.dialogData.selectedPlan.rates.forEach((rate) => {
        if (
          this.getRateSupplement(rate, 'TICKET') &&
          this.getRateSupplement(rate, 'MEAL')
        ) {
          rates.push(rate);
        }
      });
    }

    rates.forEach((rate) => {
      const ticketRate = this.getRateSupplement(rate, 'TICKET');
      if (ticketRate) {
        if (!this.supplements.hasOwnProperty(ticketRate.days)) {
          if (ticketRate.days) {
            this.supplements[ticketRate.days] = {
              BASICO: {},
              BASICO_PLUS: {},
              PARK_HOPPER: {},
              PARK_HOPPER_PLUS: {},
            };
          } else {
            console.error(`Disney ticket data error: ${ticketRate.description}`);
          }
        }
        ticketRate.rateId = rate.id;
        if (!ticketRate.flexible) {
          const flexRate = this.dialogData.selectedPlan.rates.find((r) =>
            this.getRateSupplement(r, 'TICKET')
              ? this.getRateSupplement(r, 'TICKET').flexible === true &&
                this.getRateSupplement(r, 'TICKET').days === ticketRate.days &&
                this.getRateSupplement(r, 'TICKET').ticket === ticketRate.ticket
              : null,
          );
          ticketRate.discount = {
            amount: flexRate ? this.getRateSupplement(flexRate, 'TICKET').amount : '',
            percentage: flexRate
              ? (ticketRate.amount * 100) /
                  this.getRateSupplement(flexRate, 'TICKET').amount -
                100
              : '',
          };
          if (ticketRate.days && ticketRate.ticket) {
            this.supplements[ticketRate.days][ticketRate.ticket]['no_flexible'] =
              ticketRate;
          } else {
            console.error(`Disney ticket data error: ${ticketRate.description}`);
          }
        } else {
          if (ticketRate.days && ticketRate.ticket) {
            this.supplements[ticketRate.days][ticketRate.ticket]['flexible'] = ticketRate;
          } else {
            console.error(`Disney ticket data error: ${ticketRate.description}`);
          }
        }
      }
    });
  }

  private orderTickets() {
    // Order tickets
    this.tickets = [];
    this.dialogData.selectedPlan.rates.forEach((rate) => {
      if (
        this.getRateSupplement(rate, 'TICKET') &&
        this.getRateSupplement(rate, 'TICKET').days
      ) {
        this.tickets.push({
          days: this.getRateSupplement(rate, 'TICKET').days,
          currency: this.getRateSupplement(rate, 'TICKET').currency.code,
          price: Math.ceil(this.getRateSupplement(rate, 'TICKET').amount),
          offer: this.getRateSupplement(rate, 'TICKET').offer,
        });
      }
    });
    // Order by days and price
    this.tickets.sort((a, b) => {
      if (a.days < b.days) {
        return -1;
      }
      if (a.days > b.days) {
        return 1;
      }
      if (a.price < b.price) {
        return -1;
      }
      if (a.price > b.price) {
        return 1;
      }
      return 0;
    });
    // Remove duplicated days price to get the lower price
    this.tickets = this.removeDuplicates(this.tickets, 'days');

    return this.tickets;
  }

  public getRateSupplement(rate, supplementType) {
    return this.disneyService.getRateSupplement(rate, supplementType);
  }

  public isMealPlanFree(supplement) {
    const rate = this.dialogData.selectedPlan.rates.find(
      (r) => r.id === supplement.rateId,
    );
    return this.disneyService.isMealPlanFree(rate);
  }

  public closeDialog(data?: any) {
    this.dialogRef.close(data);
  }

  /** Show ticket days selector */
  public toggleTicketSelector(index?, days?) {
    this.selectedTicketDays = index ? this.tickets[index - 1] : undefined;
    this.ticketDays = days ? days : 0;
    this.showTicketSelector = !this.showTicketSelector;
  }

  /** Select a ticket */
  public selectTicket(supplement) {
    const rate = this.dialogData.selectedPlan.rates.find(
      (r) => r.id === supplement.rateId,
    );
    const data = {
      selectedTicket: supplement.id,
      preSelectedRate: rate,
      selectedRate: rate,
      hasSameTicketForPlan: true,
      samePlanWithPromo: true,
      commission:
        this.dialogData.preSelectedRate.price.commission.value -
        this.dialogData.preSelectedRate.price.commission.tax,
    };
    this.closeDialog(data);
  }

  /** Tells whether the selected meal plan has promo tickets */
  public hasPromo(days) {
    return this.dialogData.selectedPlan.rates.some((rate) => {
      const ticket = this.getRateSupplement(rate, 'TICKET') || {};
      const meal = this.getRateSupplement(rate, 'MEAL') || {};
      return ticket.days === days && meal.amount === 0;
    });
  }

  public hasNDB(rate) {
    const meal = this.getRateSupplement(rate, 'MEAL');
    if (meal) {
      return meal.description.indexOf('NDB') !== -1;
    } else {
      return false;
    }
  }

  /**
   * Remove duplicated days price to get the lower price
   * @param arr
   * @param prop
   */
  private removeDuplicates(arr, prop) {
    const obj = {};
    for (let i = 0, len = arr.length; i < len; i++) {
      if (!obj[arr[i][prop]]) {
        obj[arr[i][prop]] = arr[i];
      }
    }
    const newArr = [];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        newArr.push(obj[key]);
      }
    }
    return newArr;
  }
}
